export function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    // A date-only string ("YYYY-MM-DD") is parsed by `new Date` as UTC midnight,
    // which renders as the previous calendar day in negative-UTC-offset zones.
    // Build it as a local date so the day never shifts; full datetimes are left
    // to the native parser (their offset is meaningful).
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(iso);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const LAST_SYNCED_PREFIX = "Last Synced At:";

/**
 * Wall time: DD, Mon, YYYY, h:mm AM/PM (12-hour).
 *
 * Defaults to the browser's local timezone. Pass `{ utc: true }` for a
 * timezone-independent rendering, which the toolbar uses during server-side
 * prerender / first paint so the SSR and hydration output match (the server
 * runs in UTC); the component then re-renders in local time after mount.
 */
export function formatOrganizationSyncTimestamp(iso, { utc = false } = {}) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(utc ? d.getUTCDate() : d.getDate()).padStart(2, "0");
  const month = d.toLocaleString(undefined, {
    month: "short",
    ...(utc ? { timeZone: "UTC" } : {}),
  });
  const year = utc ? d.getUTCFullYear() : d.getFullYear();
  const h24 = utc ? d.getUTCHours() : d.getHours();
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const minutes = String(utc ? d.getUTCMinutes() : d.getMinutes()).padStart(
    2,
    "0",
  );
  const ampm = h24 < 12 ? "AM" : "PM";
  const suffix = utc ? " UTC" : "";
  return `${day}, ${month}, ${year}, ${hour12}:${minutes} ${ampm}${suffix}`;
}

/** Last successful sync for this toolbar, in the browser's local timezone. */
export function formatOrganizationSyncStatusLine(
  lastSyncedAtIso,
  options = {},
) {
  if (!lastSyncedAtIso) {
    return `${LAST_SYNCED_PREFIX} no successful sync yet`;
  }
  const formatted = formatOrganizationSyncTimestamp(lastSyncedAtIso, options);
  if (!formatted) {
    return `${LAST_SYNCED_PREFIX} no successful sync yet`;
  }
  return `${LAST_SYNCED_PREFIX} ${formatted}`;
}
